import { useId } from "react";

/**
 * BiotechVisualization
 * Abstract molecular/botanical compound shape rendered from flowing blue
 * monospace code text along bond paths. Scientific data-viz, not decorative.
 */
export default function BiotechVisualization({ className = "" }) {
  const rawId = useId();
  const uid = rawId.replace(/[:]/g, "");
  const glowId = `bv-glow-${uid}`;
  const softGlowId = `bv-soft-${uid}`;

  // Hexagonal molecule
  const cx = 240;
  const cy = 240;
  const r = 105;
  const angles = [0, 60, 120, 180, 240, 300];
  const nodes = angles.map((a) => {
    const rad = ((a - 90) * Math.PI) / 180;
    return {
      x: cx + r * Math.cos(rad),
      y: cy + r * Math.sin(rad),
    };
  });

  const bonds = nodes.map((n, i) => {
    const next = nodes[(i + 1) % nodes.length];
    return {
      id: `bv-bond-${uid}-${i}`,
      from: n,
      to: next,
    };
  });

  // Short monospace strings sized to fit along each hex side (~105px)
  const codeStrings = [
    "0xA4F2·1B8C·4E9A",
    "GCTA·AGGC·TGCA·AA",
    "C8H10N4O2 ›› 194.19",
    "PROC_04A2 :: OK",
    "λ-13 ENTROPY 0.42",
    "SHA3 ACK 0xDE7B",
  ];

  // Outer scientific annotations
  const annotations = [
    { x: 30, y: 30, text: "SOPHIA::INFINITY/v0.13", color: "#D4AF37", op: 0.55 },
    { x: 360, y: 30, text: "REC // 04A2-LIVE", color: "#D4AF37", op: 0.55 },
    { x: 30, y: 466, text: "λ:WELLNESS_GRID", color: "#D4AF37", op: 0.55 },
    { x: 332, y: 466, text: "DRAFT — NDA ONLY", color: "#D4AF37", op: 0.55 },
    { x: 56, y: 100, text: "NODE_01 · VALENCE 4", color: "#F5F5F5", op: 0.55 },
    { x: 330, y: 100, text: "NODE_02 · VALENCE 3", color: "#F5F5F5", op: 0.55 },
    { x: 360, y: 248, text: "BOND_INTEGRITY 99.7%", color: "#F5F5F5", op: 0.55 },
    { x: 304, y: 400, text: "ASSAY_RUN #04A2", color: "#F5F5F5", op: 0.55 },
    { x: 76, y: 400, text: "EIGENSTATE COHERENT", color: "#F5F5F5", op: 0.55 },
    { x: 14, y: 248, text: "λ-CHANNEL OPEN", color: "#F5F5F5", op: 0.55 },
  ];

  const monoFont = '"JetBrains Mono", "Fira Code", ui-monospace, monospace';

  return (
    <div
      className={`relative ${className}`}
      aria-hidden="true"
      data-testid="biotech-visualization"
    >
      <svg
        viewBox="0 0 480 480"
        className="w-full h-auto bv-pulse"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <filter id={glowId} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2.4" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id={softGlowId} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="4" />
          </filter>
          {bonds.map((b) => (
            <path
              key={b.id}
              id={b.id}
              d={`M ${b.from.x} ${b.from.y} L ${b.to.x} ${b.to.y}`}
              fill="none"
            />
          ))}
        </defs>

        {/* Subtle crosshair grid */}
        <g stroke="#C0C0C0" strokeOpacity="0.06" strokeWidth="0.5">
          <line x1="0" y1={cy} x2="480" y2={cy} />
          <line x1={cx} y1="0" x2={cx} y2="480" />
        </g>

        {/* Concentric orbit rings */}
        <circle
          cx={cx}
          cy={cy}
          r="158"
          fill="none"
          stroke="#C0C0C0"
          strokeOpacity="0.18"
          strokeDasharray="1 5"
        />
        <circle
          cx={cx}
          cy={cy}
          r="195"
          fill="none"
          stroke="#C0C0C0"
          strokeOpacity="0.1"
          strokeDasharray="2 10"
        />

        {/* Soft blue glow halo behind molecule */}
        <circle
          cx={cx}
          cy={cy}
          r={r + 6}
          fill="#C0C0C0"
          opacity="0.06"
          filter={`url(#${softGlowId})`}
        />

        {/* Bond lines */}
        <g stroke="#C0C0C0" strokeOpacity="0.4" strokeWidth="1" fill="none">
          {bonds.map((b) => (
            <use key={`use-${b.id}`} href={`#${b.id}`} />
          ))}
        </g>

        {/* Center-to-outer spokes */}
        <g
          stroke="#C0C0C0"
          strokeOpacity="0.18"
          strokeWidth="0.7"
          strokeDasharray="2 3"
        >
          {nodes.map((n, i) => (
            <line key={`spk-${i}`} x1={cx} y1={cy} x2={n.x} y2={n.y} />
          ))}
        </g>

        {/* Code text along bonds */}
        <g
          fontFamily={monoFont}
          fontSize="9"
          letterSpacing="0.5"
          fill="#D9D9D9"
          filter={`url(#${glowId})`}
        >
          {bonds.map((b, i) => (
            <text key={`txt-${b.id}`} dy="-4">
              <textPath href={`#${b.id}`} startOffset="2">
                {codeStrings[i]}
              </textPath>
            </text>
          ))}
        </g>

        {/* Center node */}
        <g filter={`url(#${glowId})`}>
          <circle
            cx={cx}
            cy={cy}
            r="10"
            fill="#030303"
            stroke="#D9D9D9"
            strokeWidth="1.4"
          />
          <circle cx={cx} cy={cy} r="3.2" fill="#F5F5F5" />
        </g>

        {/* Outer nodes */}
        <g filter={`url(#${glowId})`}>
          {nodes.map((n, i) => (
            <g key={`node-${i}`}>
              <circle
                cx={n.x}
                cy={n.y}
                r="6.5"
                fill="#030303"
                stroke="#D9D9D9"
                strokeWidth="1.2"
              />
              <circle cx={n.x} cy={n.y} r="2.2" fill="#F5F5F5" />
            </g>
          ))}
        </g>

        {/* Corner brackets — instrument frame */}
        <g stroke="#D4AF37" strokeOpacity="0.45" strokeWidth="1" fill="none">
          <path d="M 14 24 L 14 14 L 24 14" />
          <path d="M 456 14 L 466 14 L 466 24" />
          <path d="M 466 456 L 466 466 L 456 466" />
          <path d="M 24 466 L 14 466 L 14 456" />
        </g>

        {/* Annotations */}
        <g fontFamily={monoFont} fontSize="6.5">
          {annotations.map((a, i) => (
            <text
              key={`ann-${i}`}
              x={a.x}
              y={a.y}
              fill={a.color}
              opacity={a.op}
              letterSpacing="0.6"
            >
              {a.text}
            </text>
          ))}
        </g>
      </svg>
    </div>
  );
}
